import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApi } from "@/lib/api";
import {
  T,
  sans,
  mono,
  AGENT_COLOR,
  TicketModal,
  type AgentRow,
  type TicketRow,
  type Memory,
} from "../operator/OperatorPage";

const STATUS_TONE: Record<string, { bg: string; color: string; label: string }> = {
  todo: { bg: T.panel2, color: T.ink2, label: "To do" },
  in_progress: { bg: T.skySoft, color: T.skyDark, label: "Live" },
  review: { bg: T.amberSoft, color: T.amber, label: "Review" },
  done: { bg: T.greenSoft, color: T.green, label: "Done" },
  archived: { bg: T.line, color: T.ink3, label: "Archived" },
};

function timeAgo(iso: string): string {
  const sec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  return `${Math.floor(hr / 24)}d`;
}

function AgentCard({
  agent,
  tickets,
  recentMemory,
  onOpenTicket,
}: {
  agent: AgentRow;
  tickets: TicketRow[];
  recentMemory: Memory | null;
  onOpenTicket: (slug: string) => void;
}) {
  const navigate = useNavigate();
  const color = AGENT_COLOR[agent.name] ?? T.skyDark;
  const isLive = agent.current_ticket_status === "in_progress";
  const liveTicket = tickets.find(
    (t) => t.id === agent.current_ticket_id,
  ) ??
    tickets.find((t) => t.status === "in_progress") ??
    null;
  const otherTickets = tickets.filter(
    (t) =>
      t.id !== liveTicket?.id && t.status !== "done" && t.status !== "archived",
  );

  return (
    <div
      style={{
        background: T.panel,
        border: `1px solid ${T.line}`,
        borderTop: `3px solid ${color}`,
        borderRadius: 10,
        padding: "16px 18px",
        display: "flex",
        flexDirection: "column",
        gap: 12,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          cursor: "pointer",
        }}
        onClick={() => navigate("/agents")}
      >
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            background: `${color}22`,
            color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontFamily: sans,
            fontSize: 18,
            fontWeight: 700,
            border: `1.5px solid ${color}66`,
            flexShrink: 0,
          }}
        >
          {agent.display_name.charAt(0)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: sans,
              fontSize: 15,
              fontWeight: 700,
              color: T.ink,
              letterSpacing: "-0.01em",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            {agent.display_name}
            {isLive && (
              <span
                className="animate-pulse"
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  background: T.green,
                  display: "inline-block",
                }}
              />
            )}
          </div>
          <div
            style={{
              fontFamily: mono,
              fontSize: 10,
              color: T.ink3,
              marginTop: 2,
            }}
          >
            @{agent.name} · {agent.in_flight_count} in flight
          </div>
        </div>
      </div>

      <div>
        <div
          style={{
            fontFamily: sans,
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: T.ink3,
            marginBottom: 5,
          }}
        >
          Current
        </div>
        {liveTicket ? (
          <div
            onClick={() => onOpenTicket(liveTicket.slug)}
            style={{
              padding: "10px 12px",
              border: `1px solid ${T.line}`,
              borderLeft: `3px solid ${color}`,
              borderRadius: 8,
              background: T.bg,
              cursor: "pointer",
              transition: "background 0.15s ease",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = T.panel2)}
            onMouseLeave={(e) => (e.currentTarget.style.background = T.bg)}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 4,
                flexWrap: "wrap",
              }}
            >
              <code
                style={{
                  fontFamily: mono,
                  fontSize: 9.5,
                  color: T.skyDark,
                  background: T.skySoft,
                  padding: "1px 6px",
                  borderRadius: 3,
                  fontWeight: 700,
                }}
              >
                {liveTicket.slug}
              </code>
              <span
                style={{
                  background: STATUS_TONE[liveTicket.status].bg,
                  color: STATUS_TONE[liveTicket.status].color,
                  padding: "2px 7px",
                  borderRadius: 999,
                  fontFamily: sans,
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                {STATUS_TONE[liveTicket.status].label}
              </span>
              {liveTicket.current_step && (
                <span
                  style={{ fontFamily: mono, fontSize: 9.5, color: T.ink3 }}
                >
                  {liveTicket.current_step}
                </span>
              )}
            </div>
            <div
              style={{
                fontFamily: sans,
                fontSize: 13,
                fontWeight: 600,
                color: T.ink,
                lineHeight: 1.35,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {liveTicket.title}
            </div>
            {liveTicket.log && (
              <div
                style={{
                  marginTop: 6,
                  fontFamily: sans,
                  fontSize: 11,
                  color: T.ink2,
                  fontStyle: "italic",
                  lineHeight: 1.4,
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {liveTicket.log
                  .split("\n")
                  .filter((l) => l.trim())
                  .slice(-1)[0]}
              </div>
            )}
          </div>
        ) : (
          <div
            style={{
              padding: "10px 12px",
              border: `1px dashed ${T.line}`,
              borderRadius: 8,
              fontFamily: sans,
              fontSize: 12,
              color: T.ink3,
              fontStyle: "italic",
              textAlign: "center",
            }}
          >
            Idle — no active ticket
          </div>
        )}
      </div>

      {otherTickets.length > 0 && (
        <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
          <div
            style={{
              fontFamily: sans,
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: T.ink3,
              marginBottom: 5,
            }}
          >
            Queue · {otherTickets.length}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {otherTickets.slice(0, 3).map((t) => (
              <div
                key={t.id}
                onClick={() => onOpenTicket(t.slug)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "5px 8px",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontFamily: sans,
                  fontSize: 11.5,
                  color: T.ink,
                  transition: "background 0.15s ease",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = T.panel2)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <span
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    background: STATUS_TONE[t.status].color,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                  }}
                  title={t.title}
                >
                  {t.title}
                </span>
                <code
                  style={{
                    fontFamily: mono,
                    fontSize: 9,
                    color: T.ink3,
                    flexShrink: 0,
                  }}
                >
                  {t.slug}
                </code>
              </div>
            ))}
          </div>
        </div>
      )}

      {recentMemory && (
        <div
          style={{
            paddingTop: 10,
            borderTop: `1px solid ${T.line}`,
            fontFamily: sans,
            fontSize: 11,
            color: T.ink2,
            lineHeight: 1.4,
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          <span
            style={{
              color: T.ink3,
              fontFamily: mono,
              fontSize: 10,
              marginRight: 6,
            }}
          >
            {timeAgo(recentMemory.created_at)} ago
          </span>
          {recentMemory.title ?? recentMemory.body.split("\n")[0]}
        </div>
      )}
    </div>
  );
}

export function AgentGalleryView() {
  const api = useApi();
  const [agents, setAgents] = useState<AgentRow[] | null>(null);
  const [tickets, setTickets] = useState<TicketRow[]>([]);
  const [memories, setMemories] = useState<Memory[]>([]);
  const [openSlug, setOpenSlug] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function fetchAll() {
      try {
        const [aRes, tRes, mRes] = await Promise.all([
          api("/api/agents"),
          api("/api/tickets"),
          api("/api/memories?type=session_log"),
        ]);
        if (cancelled) return;
        if (aRes.ok) setAgents((await aRes.json()) as AgentRow[]);
        if (tRes.ok) setTickets((await tRes.json()) as TicketRow[]);
        if (mRes.ok) setMemories((await mRes.json()) as Memory[]);
      } catch {
        // ignore
      }
    }
    void fetchAll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const orderedAgents =
    agents?.slice().sort((a, b) => {
      const aLive = a.current_ticket_status === "in_progress" ? 1 : 0;
      const bLive = b.current_ticket_status === "in_progress" ? 1 : 0;
      if (aLive !== bLive) return bLive - aLive;
      if (a.in_flight_count !== b.in_flight_count) {
        return b.in_flight_count - a.in_flight_count;
      }
      return a.display_name.localeCompare(b.display_name);
    }) ?? [];

  const liveCount = orderedAgents.filter(
    (a) => a.current_ticket_status === "in_progress",
  ).length;
  const inFlight = tickets.filter(
    (t) => t.status === "in_progress" || t.status === "todo" || t.status === "review",
  ).length;
  const inProgressNow = tickets.filter((t) => t.status === "in_progress").length;
  const doneToday = tickets.filter(
    (t) =>
      t.status === "done" &&
      Date.now() - new Date(t.updated_at).getTime() < 86_400_000,
  ).length;

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
        {[
          {
            label: "Agents online",
            value: orderedAgents.length.toString(),
            sub: `${liveCount} live`,
          },
          {
            label: "Tickets in flight",
            value: inFlight.toString(),
            sub: `${inProgressNow} in progress`,
          },
          {
            label: "Sessions logged",
            value: memories.length.toString(),
            sub: "session memories",
          },
          {
            label: "Done today",
            value: doneToday.toString(),
            sub: "last 24h",
          },
        ].map((kpi) => (
          <div
            key={kpi.label}
            style={{
              flex: 1,
              background: T.panel,
              border: `1px solid ${T.line}`,
              borderRadius: 8,
              padding: "10px 14px",
            }}
          >
            <div
              style={{
                fontFamily: sans,
                fontSize: 9,
                fontWeight: 700,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: T.ink3,
                marginBottom: 4,
              }}
            >
              {kpi.label}
            </div>
            <div
              style={{
                fontFamily: mono,
                fontSize: 22,
                fontWeight: 600,
                color: T.ink,
                lineHeight: 1,
              }}
            >
              {kpi.value}
            </div>
            <div
              style={{
                fontFamily: sans,
                fontSize: 11,
                color: T.ink3,
                marginTop: 4,
              }}
            >
              {kpi.sub}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gridTemplateRows: "repeat(2, minmax(0, 1fr))",
          gap: 12,
          flex: 1,
          minHeight: 0,
        }}
      >
        {orderedAgents.length === 0 ? (
          <div
            style={{
              gridColumn: "1 / -1",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: sans,
              fontSize: 13,
              color: T.ink3,
            }}
          >
            Loading agents…
          </div>
        ) : (
          orderedAgents.slice(0, 6).map((a) => {
            const agentTickets = tickets.filter((t) => t.agent === a.name);
            const recent = memories.find((m) => m.agent === a.name) ?? null;
            return (
              <AgentCard
                key={a.name}
                agent={a}
                tickets={agentTickets}
                recentMemory={recent}
                onOpenTicket={setOpenSlug}
              />
            );
          })
        )}
      </div>

      {openSlug && (
        <TicketModal slug={openSlug} onClose={() => setOpenSlug(null)} />
      )}
    </div>
  );
}
